import { APP_BASE_HREF } from '@angular/common'
import { Injectable, inject } from '@angular/core'
import { Params, Router } from '@angular/router'

import { AuthConfig, OAuthService } from 'angular-oauth2-oidc'
import { CookieService } from 'ngx-cookie-service'

import { BehaviorSubject, Subject, lastValueFrom, map } from 'rxjs'
import { filter, tap } from 'rxjs/operators'

import { environment } from '@flowx/designer/environment'
import { AuthApiService, ResendEmailVerificationPayload } from '@flowx/designer/shared/api/auth.api'
import { UserApiService } from '@flowx/designer/shared/api/user.api'
import { jwtUserToProfile, parseJwt } from '@flowx/shared/utils'

import {
  FX_ORGANIZATION_CODE,
  FX_ORGANIZATION_CODE_COOKIE,
  FX_ORGANIZATION_CODE_TOKEN,
} from './auth.constants'
import { setTenantId } from './tenant-id.state'

import { UserInfo, UserProfile } from '../layout/components/nav-menu/nav-menu.dictionary'
import { WorkspacesAndPermissionsStore } from '../workspaces/workspaces-and-permissions.store'

@Injectable({ providedIn: 'root' })
export class AuthConfigService {
  #userApiService = inject(UserApiService)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #cookieService = inject(CookieService)
  #authApiService = inject(AuthApiService)
  #organizationCode = inject(FX_ORGANIZATION_CODE_TOKEN)
  #baseHref = inject(APP_BASE_HREF)
  #oauthService = inject(OAuthService)
  #router = inject(Router)
  #tokenRefreshed = new Subject<void>()

  #redirectingToLogin = false

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false)
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable()
  public tokenRefreshed$ = this.#tokenRefreshed.asObservable()

  get userInfo(): UserInfo {
    return jwtUserToProfile(
      parseJwt<UserProfile>(this.#oauthService.authorizationHeader().split('Bearer ')[1])
    )
  }

  /**
   * The Keycloak SSO session (`sid`): one per browser login, shared by its tabs and kept across
   * token refreshes. Read from the token even once it expires, which still names its session.
   */
  get sessionId(): string | undefined {
    const accessToken = this.#oauthService.getAccessToken()
    return accessToken ? parseJwt<{ sid?: string }>(accessToken).sid : undefined
  }

  private get orgIdFromToken(): string | undefined {
    if (!this.#oauthService.hasValidAccessToken()) {
      return undefined
    }
    return parseJwt<{ org_id?: string }>(this.#oauthService.getAccessToken()).org_id
  }

  constructor() {
    // THe following cross-tab communication of fresh access tokens works usually in practice,
    // but if you need more robust handling the community has come up with ways to extend logic
    // in the library which may give you better mileage.
    //
    // See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    //
    // Until then we'll stick to this:
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return
      }

      console.warn(
        'Noticed changes to access_token (most likely from another tab), updating isAuthenticated'
      )
      this.isAuthenticatedSubject$.next(this.#oauthService.hasValidAccessToken())

      if (!this.#oauthService.hasValidAccessToken()) {
        this.login(this.#router.url, { message: 'loggedOutFromAnotherTab' })
      }
    })

    this.#oauthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(this.#oauthService.hasValidAccessToken())
    })
    this.isAuthenticatedSubject$.next(this.#oauthService.hasValidAccessToken())

    this.#oauthService.events
      .pipe(filter((e) => ['token_received'].includes(e.type)))
      .subscribe(() => this.#oauthService.loadUserProfile())

    this.#oauthService.events
      .pipe(filter((e) => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(() => this.navigateTo())

    this.#oauthService.events
      .pipe(filter((e) => e.type === 'token_refresh_error'))
      .subscribe(() => {
        // Guard against re-entrancy: automatic silent refresh can emit
        // `token_refresh_error` repeatedly, and each pass would launch another
        // redirect, causing concurring navigations to the login flow.
        if (this.#redirectingToLogin) {
          return
        }
        this.#redirectingToLogin = true
        // Clear the session locally (no end-session redirect) so only the
        // login flow below triggers a redirect.
        this.#oauthService.logOut(true)
        this.login(this.#router.url, { message: 'sessionExpired' })
      })

    // Handles the case when authentication happens in another tab and the current tab needs a jolt to properly init.
    this.#oauthService.events
      .pipe(filter((e) => ['invalid_nonce_in_state'].includes(e.type)))
      .subscribe(async () => {
        const data = await lastValueFrom(this.#userApiService.validateOnLogin())
        await this.#workspacesAndPermissionsStore.init(data.id)
      })

    this.#oauthService.setupAutomaticSilentRefresh()
  }

  public async runInitialLoginSequence(): Promise<void> {
    return (
      this.#oauthService
        .loadDiscoveryDocument()
        // 1. HASH LOGIN:
        // Try to log in via hash fragment after redirect back
        // from IdServer from initImplicitFlow:
        .then(() => this.#oauthService.tryLogin())
        .then(() => {
          if (this.#oauthService.hasValidAccessToken()) {
            const orgId = this.orgIdFromToken
            if (orgId) {
              setTenantId(orgId)
            }
            return Promise.resolve()
          }

          // 2. REFRESH TOKEN:
          // Try to refresh the access token using the refresh token (code flow with PKCE)
          // Only attempt refresh if we have a refresh token
          const hasRefreshToken = !!this.#oauthService.getRefreshToken()
          if (!hasRefreshToken) {
            // Return rejection to skip user validation/workspace init
            return Promise.reject({ skipAuth: true })
          }

          return this.#oauthService
            .refreshToken()
            .then(() => Promise.resolve())
            .catch((result) =>
              // If refresh token is invalid or expired, user needs to log in manually
              Promise.reject(result)
            )
        })
        .then(() =>
          // 3. Create user if it doesn't exist
          lastValueFrom(
            this.#userApiService.validateOnLogin().pipe(
              tap(({ designerUser }) => {
                // If the user is a designer user based on the validate on login respose attribute, but the token doesn't contain the DESIGNER_USER
                // role then refresh token to get latest role updates. The role might have been assigned by the validate on login request.
                if (designerUser && !this.userInfo.hasDesignerRole) {
                  this.#oauthService.refreshToken()
                }
              })
            )
          ).catch((error) => Promise.reject({ validateOnLoginError: error }))
        )
        .then((validateOnLoginData) =>
          // 4. Init workspace and permissions store
          // organisation level permissions
          // workspace level permissions
          this.#workspacesAndPermissionsStore.init(validateOnLoginData.id)
        )
        .then(() => {
          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          if (
            this.#oauthService.state &&
            this.#oauthService.state !== 'undefined' &&
            this.#oauthService.state !== 'null'
          ) {
            let stateUrl = this.#oauthService.state
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl)
            }
            this.#router.navigateByUrl(stateUrl)
          }
        })
        .catch((error) => {
          if (error.validateOnLoginError) {
            return Promise.reject(error.validateOnLoginError)
          }
          // Handle errors gracefully to not block app initialization
          // If this is just a "skip auth" signal (user not logged in), resolve gracefully
          if (error?.skipAuth) {
            // User not authenticated, app will load and guard will handle login redirect
            return Promise.resolve()
          }

          // For realm errors, navigate to login page
          if (error?.error?.error === 'Realm does not exist') {
            const organizationCode =
              this.#cookieService.get(FX_ORGANIZATION_CODE_COOKIE) || this.#organizationCode
            this.navigateTo(
              organizationCode
                ? {
                    error:
                      'Account does not exist. Check your welcome email to remember it or create a new one.',
                    data: organizationCode,
                  }
                : null
            )
          }

          // Always resolve to allow app to bootstrap
          // The guard will handle redirecting unauthenticated users
          return Promise.resolve()
        })
    )
  }

  public async login(targetUrl: string, params: Params): Promise<void> {
    // Ensure discovery document is loaded before initiating OAuth flow
    // Without this, initLoginFlow doesn't know where to redirect
    this.#oauthService
      .loadDiscoveryDocument()
      .then(() => {
        // Discovery document loaded, initiating login flow
        this.#oauthService.initLoginFlow(targetUrl || this.#router.url, params)
      })
      .catch(() => {
        // Failed to load discovery document
        // If discovery document fails to load, navigate to login page
        this.navigateTo()
      })
  }

  public logout(): void {
    this.#oauthService.logOut({ client_id: this.#oauthService.clientId })
  }

  public refreshToken(): void {
    this.#oauthService.refreshToken().then(() => {
      this.#tokenRefreshed.next()
    })
  }

  public hasValidToken(): boolean {
    return this.#oauthService.hasValidAccessToken()
  }

  public getOrganizationAuthConfig(
    runtimeAuthConfig: AuthConfig,
    organizationCode: string
  ): Promise<AuthConfig> {
    if (!organizationCode) {
      return Promise.resolve(runtimeAuthConfig)
    }
    return lastValueFrom(
      this.#authApiService.getOrganizationPublicInfo(organizationCode).pipe(
        map((authConfig) => ({
          ...runtimeAuthConfig,
          issuer: authConfig.issuer,
          redirectUri: environment.production
            ? `${authConfig.redirectUri}${this.#baseHref}?${FX_ORGANIZATION_CODE}=${encodeURIComponent(organizationCode)}`
            : `${environment.keycloak.redirectUri}${this.#baseHref}?${FX_ORGANIZATION_CODE}=${encodeURIComponent(organizationCode)}`,
          clientId: authConfig.clientId,
        }))
      )
    ).catch((error) => Promise.reject(error))
  }

  public resendEmailVerification(payload: ResendEmailVerificationPayload): Promise<void> {
    return lastValueFrom(this.#authApiService.resendEmailVerification(payload))
  }

  private navigateTo(withError: { error: string; data: string } = null): void {
    const url = new URL(window.location.href)
    const isRootOrDesignerPr = url.pathname === this.#baseHref
    const pathName = isRootOrDesignerPr ? 'start' : url.pathname
    const params: Record<string, string> = {}
    url.searchParams.forEach((value, key) => {
      params[key] = value
    })
    this.#router.navigate([pathName], {
      state: { withError },
      queryParams: params,
    })
  }
}
